/**
 * One task line. Desktop: 36px with hover actions. Phone: 56px, two lines, swipe right to
 * complete, swipe left for Snooze / Reschedule / More.
 */
import {
  memo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import {
  BellRing,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  CornerDownRight,
  MoreHorizontal,
} from 'lucide-react';
import { todayInTz, type List, type Space, type Status, type Tag, type Task } from '@karya/core';
import { cn } from '@/lib/cn';
import * as act from '@/data/actions';
import * as m from '@/data/mutations';
import { DueLabel } from './due';
import { PriorityFlag } from './priority';
import { StatusIcon, StatusMenu } from './status';
import { TagList } from './tags';
import { DatePicker } from './date-picker';
import { TaskContextMenu, TaskMoreMenu } from './task-menu';
import { toggleSelected } from './SelectionBar';
import { useUI } from '@/state/ui';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '@/components/ui/menu';
import { Cell } from '@/components/ui/grid';

export interface TaskRowProps {
  task: Task;
  status?: Status;
  statuses: Status[];
  tags: Tag[];
  space?: Space;
  list?: List;
  now: Date;
  tz: string;
  variant: 'mytasks' | 'list' | 'compact';
  phone?: boolean;
  /** Indent level for subtasks in list view. */
  depth?: number;
  subtaskCount?: number;
  expanded?: boolean;
  onToggleExpand?: () => void;
  pending?: boolean;
  focused?: boolean;
  selected?: boolean;
  onOpen: (task: Task) => void;
  onFocus?: (task: Task) => void;
  showLocation?: boolean;
  columns?: { due: boolean; priority: boolean; tags: boolean };
  dragHandle?: ReactNode;
}

function Location({ space, list }: { space?: Space; list?: List }) {
  if (!list) return null;
  return (
    <span className="min-w-0 shrink truncate text-sm text-ink-3">
      {space ? `${space.name} / ` : ''}
      {list.emoji ? `${list.emoji} ` : ''}
      {list.name}
    </span>
  );
}

export const TaskRow = memo(function TaskRow(props: TaskRowProps) {
  const { task, status, statuses, tags, now, tz, variant, phone, onOpen } = props;
  const complete = status?.type === 'done' || status?.type === 'closed';
  const isSelected = useUI((s) => s.selection.includes(task.id));
  // Hover actions (menus, date picker) mount on first hover/focus: keeps long lists light.
  const [hot, setHot] = useState(false);
  const selecting = useUI((s) => s.selection.length > 0);
  const columns = props.columns ?? { due: true, priority: true, tags: true };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter') onOpen(task);
  };

  const statusButton = (
    <StatusMenu
      statuses={statuses}
      value={task.status_id}
      onChange={(id) => void act.setStatusWithToast(task, id)}
    >
      <button
        onClick={(e) => e.stopPropagation()}
        aria-label={`Status ${status?.name ?? ''}. Change status`}
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full',
          phone ? 'size-11 -ml-2.5' : 'size-5',
        )}
      >
        <StatusIcon status={status} size={phone ? 20 : 16} />
      </button>
    </StatusMenu>
  );

  if (phone)
    return (
      <PhoneRow
        {...props}
        complete={complete}
        statusButton={statusButton}
        selected={isSelected}
        selecting={selecting}
      />
    );

  const indent = (props.depth ?? 0) * 24;
  return (
    <TaskContextMenu task={task} onOpen={() => onOpen(task)}>
      <div
        role="row"
        tabIndex={0}
        aria-selected={props.selected || isSelected}
        onKeyDown={onKeyDown}
        onFocus={() => {
          setHot(true);
          props.onFocus?.(task);
        }}
        onMouseEnter={() => setHot(true)}
        onClick={(e) => {
          if (e.ctrlKey || e.metaKey || selecting) return toggleSelected(task.id);
          onOpen(task);
        }}
        data-task-id={task.id}
        className={cn(
          'group relative flex h-8 cursor-pointer items-center gap-2 border-b border-line pr-3 outline-none',
          'hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset',
          (props.selected || isSelected) && 'bg-primary-soft',
          props.focused && 'bg-surface-2',
        )}
        style={{ paddingLeft: 12 + indent }}
      >
        <Cell>
          {props.dragHandle}
          {variant === 'list' && (
            <span className="flex w-4 shrink-0 justify-center">
              {props.subtaskCount ? (
                <button
                  aria-label={props.expanded ? 'Collapse subtasks' : 'Expand subtasks'}
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onToggleExpand?.();
                  }}
                  className="text-ink-3 hover:text-ink"
                >
                  {props.expanded ? (
                    <ChevronDown className="size-3.5" />
                  ) : (
                    <ChevronRight className="size-3.5" />
                  )}
                </button>
              ) : null}
            </span>
          )}
          {statusButton}
          <span
            className={cn(
              'min-w-0 shrink truncate',
              complete && 'text-ink-3 line-through decoration-ink-3/60',
            )}
          >
            {task.name}
          </span>
          {props.showLocation !== false && variant === 'mytasks' && (
            <>
              <span className="shrink-0 text-ink-3">·</span>
              <Location space={props.space} list={props.list} />
            </>
          )}
          {columns.tags && <TagList tags={tags} />}
          {props.subtaskCount ? (
            <span
              className="inline-flex shrink-0 items-center gap-0.5 text-cap text-ink-3"
              title={`${props.subtaskCount} subtasks`}
            >
              <CornerDownRight className="size-3" />
              {props.subtaskCount}
            </span>
          ) : null}
          {props.pending && (
            <span
              className="size-1.5 shrink-0 rounded-full bg-today"
              title="Waiting to sync"
              aria-label="Waiting to sync"
            />
          )}
          <span className="min-w-2 flex-1" />
          {hot && (
            <RowActions task={task} tz={tz} now={now} complete={complete} statuses={statuses} />
          )}
          {variant === 'list' ? (
            <>
              {columns.due && (
                <span className="w-28 shrink-0">
                  <DueLabel
                    task={task}
                    now={now}
                    tz={tz}
                    complete={complete}
                    pastStyle="relative"
                  />
                </span>
              )}
              {columns.priority && (
                <span className="flex w-20 shrink-0 items-center gap-1.5 text-sm text-ink-2">
                  <PriorityFlag priority={task.priority} />
                  {task.priority ? ['', 'Urgent', 'High', 'Normal', 'Low'][task.priority] : ''}
                </span>
              )}
            </>
          ) : (
            <>
              <span className="w-[76px] shrink-0 text-right">
                <DueLabel task={task} now={now} tz={tz} complete={complete} />
              </span>
              <span className="flex w-4 shrink-0 justify-center">
                <PriorityFlag priority={task.priority} />
              </span>
            </>
          )}
        </Cell>
      </div>
    </TaskContextMenu>
  );
});

function RowActions({
  task,
  tz,
  now,
  complete,
  statuses,
}: {
  task: Task;
  tz: string;
  now: Date;
  complete: boolean;
  statuses: Status[];
}) {
  return (
    <span
      role="toolbar"
      aria-label="Task actions"
      className="hidden shrink-0 items-center gap-0.5 group-focus-within:flex group-hover:flex"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <button
        aria-label={complete ? 'Reopen' : 'Complete'}
        title={complete ? 'Reopen (C)' : 'Complete (C)'}
        onClick={() => void act.completeWithToast(task)}
        className="flex size-[26px] items-center justify-center rounded-sm text-ink-2 hover:bg-surface-3 hover:text-done"
      >
        <Check className="size-3.5" />
      </button>
      <TaskSnoozeButton task={task} />
      <DatePicker
        value={{ start_date: task.start_date, due_date: task.due_date, due_time: task.due_time }}
        onChange={(p) => void m.updateTask(task.id, p)}
        tz={tz}
        now={now}
        recurrence={
          task.recurrence_rrule && task.recurrence
            ? { rrule: task.recurrence_rrule, options: task.recurrence }
            : null
        }
        onRecurrenceChange={(v) =>
          void m.updateTask(task.id, {
            recurrence_rrule: v?.rrule ?? null,
            recurrence: v?.options ?? null,
          })
        }
        statuses={statuses}
        align="end"
      >
        <button
          aria-label="Due date"
          title="Due date (D)"
          className="flex size-[26px] items-center justify-center rounded-sm text-ink-2 hover:bg-surface-3 hover:text-ink"
        >
          <CalendarDays className="size-3.5" />
        </button>
      </DatePicker>
      <TaskMoreMenu task={task}>
        <button
          aria-label="More actions"
          className="flex size-[26px] items-center justify-center rounded-sm text-ink-2 hover:bg-surface-3 hover:text-ink"
        >
          <MoreHorizontal className="size-3.5" />
        </button>
      </TaskMoreMenu>
    </span>
  );
}

function TaskSnoozeButton({ task }: { task: Task }) {
  return (
    <TaskMoreSnooze task={task}>
      <button
        aria-label="Snooze"
        title="Snooze (S)"
        className="flex size-[26px] items-center justify-center rounded-sm text-ink-2 hover:bg-surface-3 hover:text-ink"
      >
        <BellRing className="size-3.5" />
      </button>
    </TaskMoreSnooze>
  );
}

export function TaskMoreSnooze({ task, children }: { task: Task; children: ReactNode }) {
  return (
    <Menu>
      <MenuTrigger asChild>{children}</MenuTrigger>
      <MenuContent align="end" className="w-48" onClick={(e) => e.stopPropagation()}>
        <MenuItem onSelect={() => void act.snoozeWithToast(task, '10m')}>10 minutes</MenuItem>
        <MenuItem onSelect={() => void act.snoozeWithToast(task, '1h')}>1 hour</MenuItem>
        <MenuItem onSelect={() => void act.snoozeWithToast(task, 'tomorrow')}>
          Tomorrow morning
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

// ---------------------------------------------------------------------------
// phone row with swipe gestures
// ---------------------------------------------------------------------------

const SWIPE_COMPLETE = 96;
const ACTIONS_WIDTH = 228;

function PhoneRow(
  props: TaskRowProps & {
    complete: boolean;
    statusButton: ReactNode;
    selected: boolean;
    selecting: boolean;
  },
) {
  const { task, tags, now, tz, complete, onOpen } = props;
  const [dx, setDx] = useState(0);
  const [open, setOpen] = useState(false);
  const start = useRef<{ x: number; y: number; base: number; locked: 'h' | 'v' | null } | null>(
    null,
  );
  const moved = useRef(false);
  const longPress = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const longPressed = useRef(false);

  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType === 'mouse') return;
    start.current = { x: e.clientX, y: e.clientY, base: open ? -ACTIONS_WIDTH : 0, locked: null };
    moved.current = false;
    longPressed.current = false;
    clearTimeout(longPress.current);
    // Long-press selects the task (multi-select).
    longPress.current = setTimeout(() => {
      if (moved.current || start.current?.locked) return;
      longPressed.current = true;
      navigator.vibrate?.(12);
      toggleSelected(task.id);
    }, 480);
  };
  const onPointerMove = (e: PointerEvent) => {
    const s = start.current;
    if (!s) return;
    const ddx = e.clientX - s.x;
    const ddy = e.clientY - s.y;
    if (!s.locked) {
      if (Math.abs(ddx) < 8 && Math.abs(ddy) < 8) return;
      s.locked = Math.abs(ddx) > Math.abs(ddy) ? 'h' : 'v';
      clearTimeout(longPress.current);
    }
    if (s.locked !== 'h') return;
    moved.current = true;
    setDx(Math.max(-ACTIONS_WIDTH - 40, Math.min(160, s.base + ddx)));
  };
  const onPointerUp = () => {
    clearTimeout(longPress.current);
    const s = start.current;
    start.current = null;
    if (!s || s.locked !== 'h') return;
    if (dx > SWIPE_COMPLETE) {
      setDx(0);
      setOpen(false);
      void act.completeWithToast(task);
      return;
    }
    if (dx < -ACTIONS_WIDTH / 2) {
      setDx(-ACTIONS_WIDTH);
      setOpen(true);
    } else {
      setDx(0);
      setOpen(false);
    }
  };
  const close = () => {
    setDx(0);
    setOpen(false);
  };
  const today = todayInTz(tz, now);

  return (
    <div
      role="row"
      className="relative overflow-hidden border-b border-line"
      data-task-id={task.id}
    >
      {dx > 0 && (
        <div
          aria-hidden="true"
          className="absolute inset-0 flex items-center gap-2 bg-done pl-5 font-semibold text-white"
        >
          <Check className="size-5" />
          {complete ? 'Reopen' : 'Complete'}
        </div>
      )}
      {dx < 0 && (
        <div
          role="gridcell"
          className="absolute inset-y-0 right-0 flex"
          style={{ width: ACTIONS_WIDTH }}
        >
          <button
            className="flex flex-1 flex-col items-center justify-center gap-0.5 bg-info text-[11px] text-white"
            onClick={() => {
              close();
              void act.snoozeWithToast(task, '1h');
            }}
          >
            <BellRing className="size-[18px]" />
            Snooze 1h
          </button>
          <button
            className="flex flex-1 flex-col items-center justify-center gap-0.5 bg-today text-[11px] text-white"
            onClick={() => {
              close();
              void m.updateTask(task.id, { due_date: m.quickDate('tomorrow', today) });
            }}
          >
            <CalendarDays className="size-[18px]" />
            Tomorrow
          </button>
          <TaskMoreMenu task={task}>
            <button className="flex flex-1 flex-col items-center justify-center gap-0.5 bg-ink-3 text-[11px] text-white">
              <MoreHorizontal className="size-[18px]" />
              More
            </button>
          </TaskMoreMenu>
        </div>
      )}
      <div
        role="gridcell"
        tabIndex={0}
        aria-selected={props.selected}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={() => {
          if (moved.current || longPressed.current) return;
          if (open) return close();
          if (props.selecting) return toggleSelected(task.id);
          onOpen(task);
        }}
        onKeyDown={(e) => e.key === 'Enter' && onOpen(task)}
        className={cn(
          'relative flex min-h-[52px] touch-pan-y items-center gap-3 px-4 py-2 transition-transform duration-150 select-none',
          props.selected ? 'bg-primary-soft' : 'bg-bg',
        )}
        style={{ transform: `translateX(${dx}px)`, paddingLeft: 16 + (props.depth ?? 0) * 20 }}
      >
        {props.statusButton}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span
            className={cn(
              'truncate text-[14px] leading-[19px]',
              complete && 'text-ink-3 line-through',
            )}
          >
            {task.name}
          </span>
          <span className="flex min-w-0 items-center gap-1.5">
            {props.showLocation !== false && <Location space={props.space} list={props.list} />}
            {tags.length > 0 && <TagList tags={tags} max={1} />}
            {props.pending && (
              <span
                className="size-1.5 shrink-0 rounded-full bg-today"
                aria-label="Waiting to sync"
              />
            )}
          </span>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <DueLabel task={task} now={now} tz={tz} complete={complete} />
          <PriorityFlag priority={task.priority} size={14} />
        </div>
      </div>
    </div>
  );
}
